import { Box, Stack, Typography } from '@mui/material';
import React from 'react';

import { colors } from '../../theme/theme';
import { GenericSection, GENERIC_TEXT_RULES } from '../IMessageBubbleInterface';
import {
    capitalizeWithReplacements,
    findValueByAliases,
    highlightSupportWord,
    parseGenericSections,
} from '../MessageBubbleHelpers';

import { AssetAvatarIcon, AssetFieldGrid, AssetLocationTile, ContractSerialOnlyAssetCard } from './sharedCardHelpers';

type SectionAssetInfo = {
    assetName: string;
    assetDescription: string;
    serialNumber: string;
    make: string;
    model: string;
    year: string;
    contractId: string;
    address: string;
    city: string;
    state: string;
    postalCode: string;
    country: string;
    locationFromMetric: string;
    looksLikeAssetSection: boolean;
};

const normalizeGenericText = (value: string) => capitalizeWithReplacements(value, GENERIC_TEXT_RULES);

// A "contract serial-only" section carries just enough to identify the contract/serial pair,
// with none of the richer asset fields — it renders as the compact ContractSerialOnlyAssetCard
// instead of the full asset card.
const isContractSerialOnlyAssetSection = (info: SectionAssetInfo): boolean =>
    Boolean(
        info.serialNumber &&
            info.contractId &&
            !info.assetName &&
            !info.assetDescription &&
            !info.make &&
            !info.model &&
            !info.year &&
            !info.locationFromMetric &&
            !info.address &&
            !info.city &&
            !info.state &&
            !info.postalCode &&
            !info.country,
    );

const AssetSectionCard: React.FC<{ info: SectionAssetInfo; assetNumber: number; assetTotal: number }> = ({
    info,
    assetNumber,
    assetTotal,
}) => {
    const {
        assetName,
        assetDescription,
        serialNumber,
        make,
        model,
        year,
        contractId,
        address,
        city,
        state,
        postalCode,
        country,
        locationFromMetric,
    } = info;

    if (isContractSerialOnlyAssetSection(info)) {
        return <ContractSerialOnlyAssetCard contractId={contractId} serialNumber={serialNumber} />;
    }

    const locationParts = [address, city, state, postalCode, country].filter(Boolean);
    const location = locationFromMetric || (locationParts.length ? locationParts.join(', ') : 'N/A');
    const title = assetDescription || assetName || 'Asset';
    const subtitle = assetName && assetDescription && assetName !== assetDescription ? assetName : '';

    return (
        <Stack gap={0.6}>
            {assetTotal > 1 && (
                <Typography variant="body2" sx={{ color: '#5D7D5E', fontWeight: 700, fontSize: '0.86rem' }}>
                    Asset {assetNumber} of {assetTotal}
                </Typography>
            )}

            <Box
                sx={{
                    border: `1px solid ${colors.greyPreset[20]}`,
                    borderRadius: '14px',
                    backgroundColor: colors.fullWhite,
                    overflow: 'hidden',
                    boxShadow: '0 1px 6px rgba(16, 24, 40, 0.06)',
                    px: 1.1,
                    py: 1,
                }}
            >
                <Stack direction="row" gap={0.85} alignItems="center" sx={{ mb: 0.95 }}>
                    <AssetAvatarIcon />

                    <Box sx={{ minWidth: 0 }}>
                        <Typography
                            variant="body2"
                            sx={{ color: colors.fullBlack, fontWeight: 700, fontSize: '0.95rem', lineHeight: 1.25 }}
                        >
                            {title}
                        </Typography>
                        {contractId && (
                            <Typography
                                variant="body2"
                                sx={{ color: colors.fullBlack, fontWeight: 700, mt: 0.05, lineHeight: 1.3 }}
                            >
                                {`Contract ID: ${contractId}`}
                            </Typography>
                        )}
                        <Typography variant="body2" sx={{ color: colors.greyPreset[60], fontWeight: 500, mt: 0.05 }}>
                            {subtitle || assetName || (contractId ? `Contract ${contractId}` : 'Asset')}
                        </Typography>
                    </Box>
                </Stack>

                <Stack gap={0.8}>
                    <AssetFieldGrid
                        keyPrefix="asset"
                        fields={[
                            ['Serial Number', serialNumber || 'N/A'],
                            ['Make', make || 'N/A'],
                        ]}
                    />

                    <AssetFieldGrid
                        keyPrefix="asset"
                        fields={[
                            ['Model', model || 'N/A'],
                            ['Year', year || 'N/A'],
                        ]}
                    />

                    <AssetLocationTile location={location} />
                </Stack>
            </Box>
        </Stack>
    );
};

const GenericSectionBlock: React.FC<{ section: GenericSection; index: number }> = ({ section, index }) => (
    <Box
        sx={{
            border: `1px solid ${colors.greyPreset[30]}`,
            borderRadius: '12px',
            p: 1.25,
            backgroundColor: colors.fullWhite,
            borderLeft: `4px solid ${colors.primaryLight[100]}`,
        }}
    >
        {section.title && (
            <Typography variant="body2" sx={{ color: colors.fullBlack, fontWeight: 700, mb: 0.75, fontSize: '1rem' }}>
                {highlightSupportWord(normalizeGenericText(section.title))}
            </Typography>
        )}

        {section.summaryLines.map((line, summaryIndex) => (
            <Typography
                key={`summary-${index}-${summaryIndex}`}
                variant="body2"
                sx={{
                    color: colors.fullBlack,
                    lineHeight: 1.6,
                    mb: summaryIndex === section.summaryLines.length - 1 ? 0 : 0.5,
                }}
            >
                {highlightSupportWord(normalizeGenericText(line))}
            </Typography>
        ))}

        {section.numberedItems.length > 0 && (
            <Stack gap={0.75} sx={{ mt: section.summaryLines.length ? 1 : 0 }}>
                {section.numberedItems.map((item, itemIndex) => (
                    <Stack
                        key={`item-${index}-${itemIndex}`}
                        direction="row"
                        alignItems="flex-start"
                        gap={0.85}
                        sx={{
                            p: 0.9,
                            borderRadius: '10px',
                            backgroundColor: colors.greyPreset[5],
                            border: `1px solid ${colors.greyPreset[30]}`,
                        }}
                    >
                        <Typography
                            variant="body2"
                            sx={{
                                color: colors.fullBlack,
                                fontWeight: 700,
                                minWidth: '20px',
                                fontSize: '0.8rem',
                                mt: 0.1,
                            }}
                        >
                            {itemIndex + 1}.
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{ color: colors.greyPreset[60], lineHeight: 1.55, fontSize: '0.82rem' }}
                        >
                            {highlightSupportWord(normalizeGenericText(item))}
                        </Typography>
                    </Stack>
                ))}
            </Stack>
        )}

        {section.keyValues.length > 0 && (
            <Stack
                direction="row"
                flexWrap="wrap"
                gap={0.75}
                useFlexGap
                sx={{ mt: section.summaryLines.length || section.numberedItems.length ? 1 : 0 }}
            >
                {section.keyValues.map(({ label, value }, kvIndex) => (
                    <Box
                        key={`kv-${index}-${kvIndex}`}
                        sx={{
                            border: `1px solid ${colors.greyPreset[30]}`,
                            borderRadius: '10px',
                            px: 1,
                            py: 0.75,
                            backgroundColor: colors.greyPreset[5],
                            minWidth: '45%',
                        }}
                    >
                        <Typography
                            variant="body2"
                            sx={{
                                display: 'block',
                                color: colors.greyPreset[50],
                                fontWeight: 600,
                                fontSize: '0.76rem',
                            }}
                        >
                            {label}
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{ display: 'block', color: colors.fullBlack, fontSize: '0.82rem' }}
                        >
                            {normalizeGenericText(value)}
                        </Typography>
                    </Box>
                ))}
            </Stack>
        )}
    </Box>
);

export const GenericBotResponseCard: React.FC<{ text: string }> = ({ text }) => {
    const sections = parseGenericSections(text);
    if (!sections) {
        return null;
    }

    const normalizedSections = sections.map(section => {
        const isDocumentUploadFlow = /you can view and upload the documents by following these steps/i.test(
            section.title || section.summaryLines[0] || '',
        );

        if (isDocumentUploadFlow && section.numberedItems.length >= 5) {
            return {
                ...section,
                numberedItems: [
                    'Navigate to Documents from the left panel',
                    'Click on upload the document and select the file from your browser and click on submit.',
                ],
            };
        }

        return section;
    });

    // Derive each section's potential asset fields exactly once — previously this same set of
    // lookups was computed twice per section (once to build assetSectionIndexes below, and again
    // inside the render loop), which was the source of a self-duplicated block.
    const sectionAssetInfo = normalizedSections.map(section => {
        const assetName = findValueByAliases(section.keyValues, ['Asset Name']);
        const assetDescription = findValueByAliases(section.keyValues, ['Asset Description', 'Description']);
        const serialNumber = findValueByAliases(section.keyValues, [
            'Serial Number',
            'Serial Number (VIN)',
            'VIN',
            'Vehicle Identification Number',
        ]);
        const make = findValueByAliases(section.keyValues, ['Make']);
        const model = findValueByAliases(section.keyValues, ['Model']);
        const year = findValueByAliases(section.keyValues, ['Year']);
        const contractId = findValueByAliases(section.keyValues, ['Contract ID']);
        const address = findValueByAliases(section.keyValues, ['Address']);
        const city = findValueByAliases(section.keyValues, ['City']);
        const state = findValueByAliases(section.keyValues, ['State']);
        const postalCode = findValueByAliases(section.keyValues, ['Postal Code']);
        const country = findValueByAliases(section.keyValues, ['Country']);
        const locationFromMetric = findValueByAliases(section.keyValues, ['Location', 'Asset Location']);
        const looksLikeAssetSection = Boolean(assetName || serialNumber || make || model || assetDescription);

        return {
            assetName,
            assetDescription,
            serialNumber,
            make,
            model,
            year,
            contractId,
            address,
            city,
            state,
            postalCode,
            country,
            locationFromMetric,
            looksLikeAssetSection,
        };
    });

    const assetSectionIndexes = sectionAssetInfo
        .map((info, idx) => (info.looksLikeAssetSection ? idx : -1))
        .filter(idx => idx >= 0);

    return (
        <Stack gap={1}>
            {normalizedSections.map((section, index) => {
                const info = sectionAssetInfo[index];
                if (info.looksLikeAssetSection) {
                    return (
                        <AssetSectionCard
                            key={`section-${index}`}
                            info={info}
                            assetNumber={assetSectionIndexes.indexOf(index) + 1}
                            assetTotal={assetSectionIndexes.length}
                        />
                    );
                }
                return <GenericSectionBlock key={`section-${index}`} section={section} index={index} />;
            })}
        </Stack>
    );
};
